import { useState, useCallback, useEffect } from "react";
import posthog from "posthog-js";

const STORAGE_KEY = "wm_analytics_consent";

export type ConsentStatus = "pending" | "accepted" | "declined";

function readConsent(): ConsentStatus {
  if (typeof window === "undefined") return "pending";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "accepted" || stored === "declined") return stored;
  return "pending";
}

// Fire a consent meta-event (prompt_shown, response). Works even when the
// user is opted-out: opt in just long enough to send the event, then restore
// opted-out state. The single meta-event has no PII — only the consent
// decision — and is needed to compute consent acceptance rate.
function captureConsentMetaEvent(event: string, props: Record<string, unknown> = {}) {
  const stored = localStorage.getItem(STORAGE_KEY);
  const wasOptedOut = stored !== "accepted";
  if (wasOptedOut) {
    posthog.opt_in_capturing({ captureEventName: false });
  }
  posthog.capture(event, props);
  if (wasOptedOut) {
    // Let the request flush before opting back out.
    setTimeout(() => posthog.opt_out_capturing(), 250);
  }
}

/**
 * Hook for managing analytics consent status.
 * Syncs across browser tabs via storage events.
 */
export function useAnalyticsConsent() {
  const [status, setStatus] = useState<ConsentStatus>(readConsent);

  const accept = useCallback((method: "banner" | "modal" = "banner") => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setStatus("accepted");
    // Notify other hook instances in same tab
    window.dispatchEvent(new Event("consent-changed"));
    // Defer PostHog SDK work — first opt-in triggers session-replay init,
    // autocapture wiring, and an identify call (~500ms blocking on cold
    // start). Letting React commit the unmount first removes the perceived
    // banner stall after the user clicks Accept.
    setTimeout(() => {
      posthog.opt_in_capturing({ captureEventName: false });
      posthog.capture("consent_response", { decision: "accepted", method });
      // Legacy event — kept for backwards compat with existing dashboards.
      posthog.capture("consent_granted", { method });
    }, 0);
  }, []);

  const decline = useCallback((method: "banner" | "modal" = "banner") => {
    localStorage.setItem(STORAGE_KEY, "declined");
    setStatus("declined");
    // Notify other hook instances in same tab
    window.dispatchEvent(new Event("consent-changed"));
    // Same deferral as accept(): keep UI responsive.
    // captureConsentMetaEvent handles opt-out itself (after the event flushes).
    setTimeout(() => {
      captureConsentMetaEvent("consent_response", { decision: "declined", method });
    }, 0);
  }, []);

  const dismiss = useCallback(() => {
    // "dismissed" = ask later; readConsent() treats it as "pending".
    localStorage.setItem(STORAGE_KEY, "dismissed");
    setTimeout(() => {
      captureConsentMetaEvent("consent_response", { decision: "dismissed", method: "banner" });
    }, 0);
  }, []);

  // Fire once when the consent prompt is first shown to the user. Powers the
  // denominator of the consent acceptance rate metric.
  const trackPromptShown = useCallback((surface: "banner" | "modal" = "banner") => {
    captureConsentMetaEvent("consent_prompt_shown", { surface });
  }, []);

  // Sync consent across browser tabs and within same tab
  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      if (e.key === STORAGE_KEY) {
        const newStatus = readConsent();
        setStatus(newStatus);
        // Defer PostHog sync so cross-tab consent changes don't stall this tab's UI.
        setTimeout(() => {
          if (newStatus === "accepted") {
            posthog.opt_in_capturing();
          } else if (newStatus === "declined") {
            posthog.opt_out_capturing();
          }
        }, 0);
      }
    }

    // Same-tab sync via custom event
    function handleConsentChanged() {
      setStatus(readConsent());
    }

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("consent-changed", handleConsentChanged);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("consent-changed", handleConsentChanged);
    };
  }, []);

  return { status, accept, decline, dismiss, trackPromptShown };
}
